import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Product, Brand, Category } from "@/types";
import { Pencil, Plus, Trash2, Image as ImageIcon, Search, Filter, AlertTriangle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { invokeAdmin } from "@/services/functions";
import ProductModal from "./products/ProductModal";

const ProductsManager = () => {
  const { toast } = useToast();

  // Helper function to sanitize error messages for users - ONLY generic messages
  const getSafeErrorMessage = (error: any): string => {
    // Always return generic Portuguese messages, no technical details
    if (error?.message?.includes('imagem') || error?.message?.includes('upload') || error?.message?.includes('file')) {
      return 'Erro no carregamento de ficheiro. Tente novamente.';
    }

    if (error?.message?.includes('produto') || error?.message?.includes('product')) {
      return 'Erro ao processar produto. Verifique os dados e tente novamente.';
    }

    if (error?.message?.includes('connect') || error?.message?.includes('network') || error?.message?.includes('servidor')) {
      return 'Erro de ligação. Verifique a sua conexão e tente novamente.';
    }

    // Default generic message for everything else
    return 'Ocorreu um erro. Tente novamente ou contacte o suporte.';
  };

  const [products, setProducts] = useState<Product[]>([]);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);

  const [brands, setBrands] = useState<Brand[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  // Search and pagination state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, inactive
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Debounced search effect
  const debouncedSearchQuery = useMemo(() => {
    const timeoutId = setTimeout(() => {
      return searchQuery;
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    fetchBrandsAndCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [pagination.page, pagination.limit]);

  // Search effect with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page === 1) {
        fetchProducts();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data, error } = await invokeAdmin('admin-manage-products', {
        action: 'list',
        search: searchQuery,
        statusFilter: statusFilter,
        pagination: {
          page: pagination.page,
          limit: pagination.limit
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao carregar produtos');
      }

      if (data.products) {
        // Transformar dados do Edge Function para o formato da aplicação
        const formattedProducts = data.products.map((item: any) => ({
          id: item.id,
          name: item.name,
          description: item.description || "",
          image: item.image_url || item.image,
          price: item.price,
          pointsPrice: item.points_required || item.points_price,
          brandId: item.brand_id || "",
          categoryId: item.category_id || "",
          subcategoryId: item.subcategory_id || "",
          stock: item.stock_quantity || item.stock,
          additionalImages: item.additional_images || [],
          status: item.status || 'active' // Map status field from backend
        }));

        setProducts(formattedProducts);

        // Update pagination info
        if (data.pagination) {
          setPagination(prev => ({
            ...prev,
            total: data.pagination.total || 0,
            totalPages: Math.ceil((data.pagination.total || 0) / pagination.limit)
          }));
        }
      }
    } catch (error: any) {
      console.error("Erro ao buscar produtos:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os produtos",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandsAndCategories = async () => {
    try {
      // Buscar marcas via Edge Function
      const { data: brandsResponse, error: brandsError } = await invokeAdmin('admin-manage-brands', { action: 'list' });

      if (brandsError) throw brandsError;
      if (!brandsResponse?.success) throw new Error('Erro ao carregar marcas');

      // Buscar categorias principais via query direta (pode ser migrado depois)
      const { data: categoriesData, error: categoriesError } = await supabase
        .from("categories")
        .select("*")
        .filter("parent_id", "is", null)
        .order("name");

      if (categoriesError) throw categoriesError;

      // Buscar subcategorias via query direta (pode ser migrado depois)
      const { data: subcategoriesData, error: subcategoriesError } = await supabase
        .from("categories")
        .select("*")
        .filter("parent_id", "not.is", null)
        .order("name");

      if (subcategoriesError) throw subcategoriesError;

      // Formatar os dados recebidos
      const formattedBrands = (brandsResponse.brands || []).map((brand: any) => ({
        id: brand.id,
        name: brand.name,
        logo: brand.logo_url || brand.logo || '',
        description: brand.description || ''
      }));

      const formattedCategories = (categoriesData || []).map(category => ({
        id: category.id,
        name: category.name,
        image: category.image || '',
        parentId: category.parent_id || undefined,
        createdAt: category.created_at
      }));

      const formattedSubcategories = (subcategoriesData || []).map(subcategory => ({
        id: subcategory.id,
        name: subcategory.name,
        image: subcategory.image || '',
        parentId: subcategory.parent_id || '',
        createdAt: subcategory.created_at
      }));

      setBrands(formattedBrands);
      setCategories(formattedCategories);
      setSubcategories(formattedSubcategories);

    } catch (error: any) {
      console.error("Erro ao buscar dados auxiliares:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar marcas e categorias",
        variant: "destructive",
      });
    }
  };

  // Modal handler functions
  const handleCloseProductModal = () => {
    setIsProductModalOpen(false);
    setEditingProduct(null);
  };

  const handleProductSaved = async () => {
    await fetchProducts();
  };

  const startEditing = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setEditingProduct(product);
      setIsProductModalOpen(true);
    } else {
      toast({
        title: "Erro",
        description: "Produto não encontrado",
        variant: "destructive",
      });
    }
  };

  const showDeleteConfirmation = (product: Product) => {
    setProductToDelete(product);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      const { data, error } = await invokeAdmin('admin-manage-products', {
        action: 'delete',
        productId: productToDelete.id
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erro ao apagar produto');

      // Apagar produto da lista
      setProducts(prev => prev.filter(product => product.id !== productToDelete.id));

      toast({
        title: "Sucesso",
        description: "Produto apagado com sucesso",
      });

      setDeleteConfirmOpen(false);
      setProductToDelete(null);
    } catch (error: any) {
      console.error("Erro ao apagar produto:", error);
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setProductToDelete(null);
  };

  const getBrandName = (brandId: string) => {
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : "Desconhecida";
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : "Desconhecida";
  };

  const getSubcategoryName = (subcategoryId?: string) => {
    if (!subcategoryId) return "-";
    const subcategory = subcategories.find(s => s.id === subcategoryId);
    return subcategory ? subcategory.name : "Desconhecida";
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination(prev => ({
      ...prev,
      limit: newLimit,
      page: 1 // Reset to first page when changing limit
    }));
  };

  // Search handler
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div>
      {/* Search Bar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-4 flex-1 max-w-2xl">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Procurar produtos..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-10 bg-brand-darker text-white border-gray-600 focus:border-brand-red"
            />
            {searchQuery && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSearchQuery("")}
                  className="h-auto p-1 text-gray-400 hover:text-white"
                >
                  ✕
                </Button>
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2 min-w-[180px]">
            <Filter className="h-4 w-4 text-gray-400" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-brand-darker text-white border-gray-600 focus:border-brand-red">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="inactive">Inativos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-400">
          {searchQuery && (
            <span>
              {pagination.total} resultado(s) para "{searchQuery}"
            </span>
          )}
          {!searchQuery && (
            <span>
              {pagination.total} produto(s) total
            </span>
          )}
        </div>
      </div>

      <Button
        onClick={() => setIsProductModalOpen(true)}
        className="mb-6 bg-brand-red text-white hover:bg-brand-red/90"
        disabled={loading}
      >
        <Plus size={16} className="mr-2" /> Criar Produto
      </Button>

      <div className="rounded-md border border-gray-700 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-brand-dark hover:bg-brand-dark/80 border-b border-gray-700">
              <TableHead className="text-white font-semibold p-4">Imagem</TableHead>
              <TableHead className="text-white font-semibold p-4">Produto</TableHead>
              <TableHead className="text-white font-semibold p-4">Preço</TableHead>
              <TableHead className="text-white font-semibold p-4">Marca</TableHead>
              <TableHead className="text-white font-semibold p-4">Categoria</TableHead>
              <TableHead className="text-white font-semibold p-4">Stock</TableHead>
              <TableHead className="text-white font-semibold p-4">Estado</TableHead>
              <TableHead className="text-white font-semibold p-4 w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-6">
                  <div className="flex justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-t-2 border-brand-red"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-6 text-gray-400">
                  Nenhum produto registado
                </TableCell>
              </TableRow>
            ) : (
              products.map(product => (
                <TableRow
                  key={product.id}
                  className="group hover:bg-brand-dark/80 transition-all duration-200 border-b border-gray-800 hover:border-brand-red/30 hover:shadow-lg hover:shadow-brand-red/10"
                >
                  <TableCell className="p-4">
                    <div className="relative overflow-hidden rounded-lg">
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-12 h-12 object-cover transition-transform duration-200 group-hover:scale-110"
                          key={product.image} // Force re-render when image URL changes
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = "/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png";
                          }}
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center group-hover:bg-gray-600 transition-colors duration-200">
                          <ImageIcon size={18} className="text-gray-400 group-hover:text-gray-300" />
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="p-4">
                    <div className="space-y-1">
                      <p className="font-medium text-white group-hover:text-brand-red transition-colors duration-200">
                        {product.name}
                      </p>
                      {product.description && (
                        <p className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors duration-200 truncate max-w-xs">
                          {product.description}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="p-4">
                    <div className="flex flex-col">
                      <span className="font-medium text-green-400">€{Number(product.price).toFixed(2)}</span>
                      <span className="text-xs text-gray-400">{product.pointsPrice} pontos</span>
                    </div>
                  </TableCell>
                  <TableCell className="p-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900/50 text-blue-300 group-hover:bg-blue-800/50 transition-colors duration-200">
                      {product.brandId ? getBrandName(product.brandId) : "Sem marca"}
                    </span>
                  </TableCell>
                  <TableCell className="p-4">
                    <div className="space-y-1">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-900/50 text-purple-300 group-hover:bg-purple-800/50 transition-colors duration-200">
                        {product.categoryId ? getCategoryName(product.categoryId) : "Sem categoria"}
                      </span>
                      {product.subcategoryId && (
                        <div className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors duration-200">
                          {getSubcategoryName(product.subcategoryId)}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="p-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        product.stock > 10 ? 'bg-green-500' :
                        product.stock > 0 ? 'bg-yellow-500' : 'bg-red-500'
                      } group-hover:scale-125 transition-transform duration-200`}></div>
                      <span className={`font-medium ${
                        product.stock > 10 ? 'text-green-400' :
                        product.stock > 0 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {product.stock}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="p-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        (product.status === 'active' || !product.status) ? 'bg-green-500' : 'bg-red-500'
                      } group-hover:scale-125 transition-transform duration-200`}></div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        (product.status === 'active' || !product.status)
                          ? 'bg-green-900/50 text-green-300'
                          : 'bg-red-900/50 text-red-300'
                      } group-hover:scale-105 transition-transform duration-200`}>
                        {(product.status === 'active' || !product.status) ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="p-4">
                    <div className="flex gap-1 opacity-70 group-hover:opacity-100 transition-opacity duration-200">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => startEditing(product.id)}
                        className="h-8 w-8 text-gray-400 hover:text-white hover:bg-brand-red/20 hover:border-brand-red transition-all duration-200"
                        title="Editar produto"
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => showDeleteConfirmation(product)}
                        className="h-8 w-8 text-gray-400 hover:text-red-400 hover:bg-red-500/20 hover:border-red-500 transition-all duration-200"
                        title="Apagar produto"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 p-4 bg-brand-dark rounded-lg">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span>Mostrando</span>
            <Select
              value={pagination.limit.toString()}
              onValueChange={(value) => handleLimitChange(parseInt(value))}
            >
              <SelectTrigger className="w-16 h-8 bg-brand-darker text-white border-gray-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>de {pagination.total} produtos</span>
          </div>

          <div className="flex items-center gap-2">
            {/* First Page */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handlePageChange(1)}
              disabled={pagination.page === 1}
              className="bg-transparent border-gray-600 text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-50"
            >
              ««
            </Button>

            {/* Previous Page */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="bg-transparent border-gray-600 text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-50"
            >
              «
            </Button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {[...Array(Math.min(5, pagination.totalPages))].map((_, index) => {
                let pageNumber;
                if (pagination.totalPages <= 5) {
                  pageNumber = index + 1;
                } else {
                  const start = Math.max(1, pagination.page - 2);
                  const end = Math.min(pagination.totalPages, start + 4);
                  pageNumber = start + index;
                  if (pageNumber > end) return null;
                }

                return (
                  <Button
                    key={pageNumber}
                    size="sm"
                    variant={pagination.page === pageNumber ? "default" : "outline"}
                    onClick={() => handlePageChange(pageNumber)}
                    className={
                      pagination.page === pageNumber
                        ? "bg-brand-red text-white hover:bg-brand-red/90"
                        : "bg-transparent border-gray-600 text-gray-400 hover:text-white hover:bg-gray-800"
                    }
                  >
                    {pageNumber}
                  </Button>
                );
              })}
            </div>

            {/* Next Page */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="bg-transparent border-gray-600 text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-50"
            >
              »
            </Button>

            {/* Last Page */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.page === pagination.totalPages}
              className="bg-transparent border-gray-600 text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-50"
            >
              »»
            </Button>
          </div>

          <div className="text-sm text-gray-400">
            Página {pagination.page} de {pagination.totalPages}
          </div>
        </div>
      )}

      {/* Product Modal */}
      <ProductModal
        isOpen={isProductModalOpen}
        onClose={handleCloseProductModal}
        editingProduct={editingProduct}
        brands={brands}
        categories={categories}
        subcategories={subcategories}
        onProductSaved={handleProductSaved}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="w-[95vw] max-w-md bg-brand-gray border-0 p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-400" />
              </div>
              Confirmar Eliminação
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-300 mb-4">
              Tem a certeza que deseja apagar o produto?
            </p>
            {productToDelete && (
              <div className="bg-brand-dark rounded-lg p-4 border border-gray-700">
                <div className="flex items-center gap-3">
                  {productToDelete.image ? (
                    <img
                      src={productToDelete.image}
                      alt={productToDelete.name}
                      className="w-12 h-12 object-cover rounded-lg"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png";
                      }}
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center">
                      <ImageIcon size={18} className="text-gray-400" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-white">{productToDelete.name}</p>
                    <p className="text-sm text-gray-400">€{Number(productToDelete.price).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            )}
            <p className="text-red-400 text-sm mt-3 font-medium">
              ⚠️ Esta ação não pode ser desfeita.
            </p>
          </div>

          <DialogFooter className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancelDelete}
              className="bg-transparent border-gray-400 text-white hover:bg-gray-600 hover:border-gray-300"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleDeleteProduct}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Sim, Apagar Produto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsManager;